import { Inject, Injectable } from '@angular/core';
import { User } from '@lobos/library';
import { captureException, init, setUser } from '@sentry/browser';
import { ErrorInterface } from '../../error';
import { SENTRY_CONFIG, SentryConfig } from '../config/sentry.config';

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  constructor(@Inject(SENTRY_CONFIG) private config: SentryConfig) {}

  public setup(): void {
    if (!this.config.active) {
      return;
    }

    init({
      release: this.config.release,
      attachStacktrace: true,
      autoSessionTracking: false,
      debug: this.config.debug,
      environment: this.config.environment,
      dsn: this.config.dsn,
      // Expired/rate-limited sessions surface as HTTP 401/429. They are expected
      // and also reach Sentry via automatic instrumentation (mechanism: instrument),
      // so drop them centrally here in addition to the explicit capture path.
      beforeSend: (event, hint) => {
        const original: any = hint?.originalException;
        const status = original?.status ?? original?.error?.status ?? original?.orginalError?.status;
        if (status === 401 || status === 429) {
          return null;
        }
        return event;
      },
    });
  }

  public updateUser(user: User): void {
    if (!this.config.active) {
      return;
    }

    setUser({ email: user ? user.sEmail || user.sUserName : undefined });
  }

  public captureException(error: ErrorInterface): void {
    if (!this.config.active) {
      return;
    }

    const originalError = error.error?.orginalError || error.error;

    // Expired/rate-limited sessions surface as HTTP 401/429 and are expected
    // behaviour, not application errors. Don't report them to Sentry.
    if (originalError?.status === 401 || originalError?.status === 429) {
      return;
    }

    captureException(originalError, {
      user: { ip_address: undefined },
      extra: error as any,
      tags: { label: error.label },
    });
  }
}
